import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AttachIcon,
  Avatar,
  Badge,
  Banner,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  ChatIcon,
  CheckIcon,
  ClockIcon,
  CloseIcon,
  Control,
  DescriptionIcon,
  Divider,
  EditIcon,
  EmptyState,
  HistoryIcon,
  Input,
  Label,
  LinkIcon,
  Modal,
  ModalContent,
  OpenInNewIcon,
  PlusIcon,
  ProgressIcon,
  RefreshIcon,
  SegmentedControl,
  SegmentedControlButton,
  SendIcon,
  Select,
  SelectOption,
  SuccessIcon,
  Tab,
  Tabs,
  TextArea,
  ToolJiraIcon,
  TrashIcon,
  UndoIcon,
  useDocumentTitle,
} from '@forge/common';
import { setRequirements, useIntakeStore } from './intakeStore';
import { formatBytes, newId } from './intakeUtils';
import {
  JIRA_FLOW,
  JIRA_STATUS_META,
  PRIORITY_META,
  STATUS_META,
  STATUS_ORDER,
} from '@/data/mock-requirements';
import type {
  ActorRole,
  Attachment,
  JiraIssue,
  JiraStatus,
  Requirement,
  RequirementEvent,
  RequirementStatus,
  RequirementVersion,
} from '@/data/mock-requirements';

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const ROLE_LABEL: Record<ActorRole, string> = {
  client: 'Client',
  account_manager: 'Account Manager',
  developer: 'Development',
};

/** Display name used when the current "View as" role performs an action. */
const ACTOR_NAME: Record<ActorRole, string> = {
  client: '', // resolved from the request's client for client actions
  account_manager: 'Marcus Reed',
  developer: 'Sofia Lindqvist',
};

const roleBadgeColor: Record<ActorRole, 'blue' | 'yellow' | 'orange'> = {
  client: 'blue',
  account_manager: 'yellow',
  developer: 'orange',
};

const formatDateTime = (iso: string): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));

const relativeTime = (iso: string): string => {
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return formatDateTime(iso);
};

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');


const allJiraDone = (req: Requirement): boolean =>
  req.jiraIssues.length > 0 && req.jiraIssues.every((i) => i.status === 'done');

type DiffLine = { type: 'ctx' | 'add' | 'del'; text: string };

/** Compact LCS-based line diff so the History tab can show before/after. */
const diffLines = (oldText: string, newText: string): DiffLine[] => {
  const a = oldText.split('\n');
  const b = newText.split('\n');
  const m = a.length;
  const n = b.length;
  const lcs: number[][] = Array.from({ length: m + 1 }, () =>
    new Array<number>(n + 1).fill(0),
  );
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      lcs[i]![j] =
        a[i] === b[j]
          ? lcs[i + 1]![j + 1]! + 1
          : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      out.push({ type: 'ctx', text: a[i]! });
      i += 1;
      j += 1;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      out.push({ type: 'del', text: a[i]! });
      i += 1;
    } else {
      out.push({ type: 'add', text: b[j]! });
      j += 1;
    }
  }
  while (i < m) {
    out.push({ type: 'del', text: a[i]! });
    i += 1;
  }
  while (j < n) {
    out.push({ type: 'add', text: b[j]! });
    j += 1;
  }
  return out;
};

/* -------------------------------------------------------------------------- */
/* Workflow stepper                                                           */
/* -------------------------------------------------------------------------- */

type StepState = 'done' | 'current' | 'cancelled' | 'upcoming';

const StageStepper = ({ requirement }: { requirement: Requirement }) => {
  const { status, cancellation } = requirement;
  const isCancelled = status === 'cancelled' && !!cancellation;
  const cancelledIndex = isCancelled
    ? STATUS_ORDER.indexOf(cancellation!.cancelledAtStatus)
    : -1;
  const currentIndex = STATUS_ORDER.indexOf(status);

  const stepState = (i: number): StepState => {
    if (isCancelled) {
      if (i < cancelledIndex) return 'done';
      if (i === cancelledIndex) return 'cancelled';
      return 'upcoming';
    }
    if (status === 'fulfilled') return 'done';
    if (i < currentIndex) return 'done';
    if (i === currentIndex) return 'current';
    return 'upcoming';
  };

  const circleClass: Record<StepState, string> = {
    done: 'bg-green-600 text-white',
    current: 'bg-blue-500 text-white ring-4 ring-blue-500/20',
    cancelled: 'bg-red-500 text-white',
    upcoming:
      'border-2 border-gray-300 bg-white text-gray-400 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-500',
  };

  const labelClass: Record<StepState, string> = {
    done: 'text-color-secondary dark:text-color-secondary-dark',
    current: 'font-semibold text-blue-600 dark:text-blue-400',
    cancelled: 'font-semibold text-red-600 dark:text-red-400',
    upcoming: 'text-color-tertiary',
  };

  const stepIcon = (state: StepState) => {
    switch (state) {
      case 'done':
        return <CheckIcon className="h-4 w-4" />;
      case 'current':
        return <ClockIcon className="h-4 w-4" />;
      case 'cancelled':
        return <CloseIcon className="h-4 w-4" />;
      case 'upcoming':
        return <span className="h-1.5 w-1.5 rounded-full bg-current" />;
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex items-start overflow-x-auto px-1 pb-1 pt-2">
        {STATUS_ORDER.map((stage, i) => {
          const state = stepState(i);
          const connectorGreen =
            i > 0 && stepState(i - 1) === 'done' && state !== 'upcoming';
          const connectorRed = state === 'cancelled';
          return (
            <Fragment key={stage}>
              {i > 0 && (
                <div
                  className={[
                    'mt-3.5 h-0.5 min-w-[1.25rem] flex-1',
                    connectorRed
                      ? 'bg-red-400'
                      : connectorGreen
                        ? 'bg-green-600'
                        : 'bg-gray-200 dark:bg-gray-700',
                  ].join(' ')}
                />
              )}
              <div className="flex min-w-[4rem] flex-col items-center gap-1.5 text-center">
                <span
                  className={[
                    'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                    circleClass[state],
                  ].join(' ')}
                >
                  {stepIcon(state)}
                </span>
                <span className={['text-xs leading-tight', labelClass[state]].join(' ')}>
                  {STATUS_META[stage].label}
                </span>
              </div>
            </Fragment>
          );
        })}
      </div>
      {isCancelled && cancellation && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">
          Cancelled at{' '}
          <span className="font-medium">
            {STATUS_META[cancellation.cancelledAtStatus].label}
          </span>{' '}
          · requested by {ROLE_LABEL[cancellation.requestedByRole]}
          {cancellation.confirmedAt
            ? ` · confirmed ${formatDateTime(cancellation.confirmedAt)}`
            : ''}
        </p>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Attachments                                                                */
/* -------------------------------------------------------------------------- */

const AttachmentRow = ({
  attachment,
  canRemove,
  onRemove,
}: {
  attachment: Attachment;
  canRemove: boolean;
  onRemove: () => void;
}) => (
  <div className="group flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-800">
    <div className="flex min-w-0 items-center gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-color-secondary dark:bg-gray-800 dark:text-color-secondary-dark">
        <AttachIcon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{attachment.name}</p>
        <p className="text-xs text-color-tertiary">
          {formatBytes(attachment.size)} · {attachment.uploadedBy} ·{' '}
          {relativeTime(attachment.uploadedAt)}
        </p>
      </div>
    </div>
    {canRemove && (
      <Button
        aria-label={`Remove ${attachment.name}`}
        variant="destructive-secondary"
        icon={<TrashIcon />}
        onClick={onRemove}
        className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
      />
    )}
  </div>
);

const AttachmentsSection = ({
  requirement,
  onAdd,
  onRemove,
}: {
  requirement: Requirement;
  onAdd: (files: { name: string; size: number }[]) => void;
  onRemove: (id: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const canEdit = requirement.status !== 'fulfilled';
  const [pendingRemove, setPendingRemove] = useState<Attachment | null>(null);

  return (
    <div>
      <Divider subtitle={`Attachments (${requirement.attachments.length})`} />
      <div className="mt-3 flex items-center justify-end">
        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []).map((f) => ({
                  name: f.name,
                  size: f.size,
                }));
                if (files.length) onAdd(files);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              icon={<PlusIcon />}
              onClick={() => inputRef.current?.click()}
            >
              Add files
            </Button>
          </>
        )}
      </div>

      {requirement.attachments.length === 0 ? (
        <p className="mt-2 text-sm text-color-tertiary">No attachments yet.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {requirement.attachments.map((a) => (
            <AttachmentRow
              key={a.id}
              attachment={a}
              canRemove={canEdit}
              onRemove={() => setPendingRemove(a)}
            />
          ))}
        </div>
      )}

      {pendingRemove && (
        <Modal
          title="Remove attachment"
          onModalClose={() => setPendingRemove(null)}
          onBackdropClick={() => setPendingRemove(null)}
          footerRight={
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => setPendingRemove(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive-primary"
                icon={<TrashIcon />}
                onClick={() => {
                  onRemove(pendingRemove.id);
                  setPendingRemove(null);
                }}
              >
                Remove
              </Button>
            </div>
          }
        >
          <ModalContent padded>
            <p className="text-sm">
              Remove <span className="font-semibold">{pendingRemove.name}</span> from this
              request? This can&apos;t be undone.
            </p>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Development (JIRA) tab                                                      */
/* -------------------------------------------------------------------------- */

const JiraIssueRow = ({ issue }: { issue: JiraIssue }) => {
  const meta = JIRA_STATUS_META[issue.status];
  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ToolJiraIcon className="h-4 w-4 text-[#2684FF]" />
          <a
            href={issue.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-sm font-semibold text-blue-600 hover:underline dark:text-blue-400"
          >
            {issue.key}
          </a>
          <Badge color="gray" outline>
            {issue.type}
          </Badge>
        </div>
        <Badge color={meta.color}>{meta.label}</Badge>
      </div>
      <p className="mt-2 text-sm">{issue.summary}</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-xs text-color-tertiary">
          <Avatar text={initialsOf(issue.assignee)} aria-label={issue.assignee} />
          {issue.assignee}
        </span>
        <Button
          as="link"
          href={issue.url}
          variant="quiet-primary"
          icon={<OpenInNewIcon />}
          target="_blank"
          rel="noopener noreferrer"
          external
        >
          Open in JIRA
        </Button>
      </div>
    </div>
  );
};

const DevelopmentTab = ({
  requirement,
  viewAs,
  onSync,
  onLinkJira,
  onCreateJira,
}: {
  requirement: Requirement;
  viewAs: ActorRole;
  onSync: () => void;
  onLinkJira: () => void;
  onCreateJira: () => void;
}) => {
  const done = allJiraDone(requirement);
  const status = requirement.status;
  // Account managers own JIRA creation and can add stories once a request is in
  // account manager review or in development. Developers may also add stories, but
  // only once the request is actually in development (never before).
  const canAddStory =
    viewAs === 'account_manager'
      ? status === 'in_account_manager' || status === 'in_development'
      : viewAs === 'developer' && status === 'in_development';

  const addStoryButtons = canAddStory ? (
    <>
      <Button variant="primary" icon={<PlusIcon />} onClick={onCreateJira}>
        Create JIRA story
      </Button>
      <Button variant="secondary" icon={<LinkIcon />} onClick={onLinkJira}>
        Link existing
      </Button>
    </>
  ) : null;

  if (requirement.jiraIssues.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={<ToolJiraIcon />}
          description={
            canAddStory
              ? 'No JIRA stories yet. Create a story or link an existing one, then send the request to development.'
              : 'No development work yet. Account managers create linked JIRA stories for this request.'
          }
        />
        {addStoryButtons && (
          <div className="flex justify-center gap-2">{addStoryButtons}</div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-color-tertiary">
          Development is tracked in JIRA. Status here mirrors the linked issues.
        </p>
        <div className="flex items-center gap-2">
          {addStoryButtons}
          {viewAs === 'developer' && !done && (
            <Button variant="secondary" icon={<RefreshIcon />} onClick={onSync}>
              Sync from JIRA (demo)
            </Button>
          )}
        </div>
      </div>

      {done ? (
        <Banner
          variant="inline"
          type="success"
          title="Ready to fulfil"
          description="All JIRA work is Done — account managers can mark this request fulfilled."
          canClose={false}
        />
      ) : (
        <Banner
          variant="inline"
          type="info"
          title="Development in progress"
          description="This request will be ready to fulfil once all linked JIRA issues are Done."
          canClose={false}
        />
      )}

      <div className="space-y-2">
        {requirement.jiraIssues.map((issue) => (
          <JiraIssueRow key={issue.key} issue={issue} />
        ))}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* History tab (version timeline + diff)                                      */
/* -------------------------------------------------------------------------- */

const DiffBlock = ({ lines }: { lines: DiffLine[] }) => (
  <div className="mt-2 overflow-x-auto rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-xs leading-relaxed dark:border-gray-800 dark:bg-gray-950/50">
    {lines.map((line, idx) => (
      <div
        key={idx}
        className={[
          'whitespace-pre-wrap break-words px-1',
          line.type === 'add'
            ? 'bg-green-100 text-green-900 dark:bg-green-500/15 dark:text-green-200'
            : line.type === 'del'
              ? 'bg-red-100 text-red-900 line-through dark:bg-red-500/15 dark:text-red-200'
              : 'text-color-secondary',
        ].join(' ')}
      >
        {line.type === 'add' ? '+ ' : line.type === 'del' ? '- ' : '  '}
        {line.text || '\u00A0'}
      </div>
    ))}
  </div>
);

const VersionCard = ({
  version,
  previous,
}: {
  version: RequirementVersion;
  previous?: RequirementVersion;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs font-semibold dark:bg-gray-800">
            v{version.version}
          </span>
          {version.causedRevert && (
            <Badge color="orange" icon={<UndoIcon />}>
              Reverted to Account Manager Review
            </Badge>
          )}
        </div>
        <span className="flex items-center gap-1 text-xs text-color-tertiary">
          <ClockIcon className="h-3.5 w-3.5" />
          {formatDateTime(version.changedAt)}
        </span>
      </div>

      <p className="mt-2 text-sm">{version.changeSummary ?? 'Content updated.'}</p>
      <p className="mt-1 text-xs text-color-tertiary">
        {version.changedBy} · {ROLE_LABEL[version.changedByRole]} · edited while{' '}
        <span className="font-medium">
          {STATUS_META[version.statusAtChange].label}
        </span>
      </p>

      {previous ? (
        <div className="mt-3">
          <Button
            variant="quiet-primary"
            icon={<HistoryIcon />}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide changes' : `Compare with v${previous.version}`}
          </Button>
          {open && <DiffBlock lines={diffLines(previous.body, version.body)} />}
        </div>
      ) : (
        <p className="mt-3 text-xs italic text-color-tertiary">
          Original version — no prior content to compare.
        </p>
      )}
    </div>
  );
};

const HistoryTab = ({ requirement }: { requirement: Requirement }) => {
  const ordered = [...requirement.versions].sort((a, b) => b.version - a.version);

  return (
    <div className="space-y-4">
      {ordered.map((version) => {
        const previous = requirement.versions.find(
          (v) => v.version === version.version - 1,
        );
        return (
          <VersionCard key={version.version} version={version} previous={previous} />
        );
      })}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Discussion tab                                                             */
/* -------------------------------------------------------------------------- */

const eventLine = (event: RequirementEvent): string => {
  switch (event.type) {
    case 'created':
      return 'created this request';
    case 'submitted':
      return 'submitted the request for review';
    case 'content_reverted':
      return 'edited the content after submission — sent back for re-review';
    case 'jira_updated':
      return 'updated development in JIRA';
    case 'attachment_added':
      return 'added an attachment';
    case 'fulfilled':
      return 'marked the request fulfilled';
    case 'status_changed':
      return `moved it from ${
        event.fromStatus ? STATUS_META[event.fromStatus].label : '—'
      } to ${event.toStatus ? STATUS_META[event.toStatus].label : '—'}`;
    default:
      return '';
  }
};

const DiscussionTab = ({
  requirement,
  onComment,
}: {
  requirement: Requirement;
  onComment: (body: string) => void;
}) => {
  const [draft, setDraft] = useState('');
  const timeline = [...requirement.events].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onComment(trimmed);
    setDraft('');
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto pr-1">
        {timeline.map((event) => {
          const isComment = event.type === 'comment';
          return (
            <div key={event.id} className="flex gap-3">
              <Avatar text={initialsOf(event.actor)} aria-label={event.actor} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold">{event.actor}</span>
                  <Badge color={roleBadgeColor[event.actorRole]} outline>
                    {ROLE_LABEL[event.actorRole]}
                  </Badge>
                  <span className="text-xs text-color-tertiary">
                    {relativeTime(event.createdAt)}
                  </span>
                </div>
                {isComment ? (
                  <p className="mt-1 whitespace-pre-wrap rounded-lg bg-gray-100 px-3 py-2 text-sm dark:bg-gray-800">
                    {event.body}
                  </p>
                ) : (
                  <p className="mt-0.5 text-sm text-color-secondary">
                    {eventLine(event)}
                    {event.body ? (
                      <span className="text-color-tertiary"> — {event.body}</span>
                    ) : null}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 border-t border-gray-200 pt-3 dark:border-gray-800">
        <Control hideSpacing="bottom">
          <TextArea
            id="req-comment"
            rows={2}
            placeholder="Add a message…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </Control>
        <div className="mt-2 flex justify-end">
          <Button
            variant="primary"
            icon={<SendIcon />}
            disabled={!draft.trim()}
            onClick={submit}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Modals                                                                     */
/* -------------------------------------------------------------------------- */

const LinkJiraModal = ({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (input: {
    key: string;
    type: JiraIssue['type'];
    summary: string;
    assignee: string;
    status: JiraStatus;
  }) => void;
}) => {
  const [key, setKey] = useState('');
  const [type, setType] = useState<JiraIssue['type']>('Story');
  const [summary, setSummary] = useState('');
  const [assignee, setAssignee] = useState('');
  const [status, setStatus] = useState<JiraStatus>('to_do');
  const keyValid = /^[A-Za-z][A-Za-z0-9]+-\d+$/.test(key.trim());

  return (
    <Modal
      title="Add JIRA story"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerRight={
        <Button
          variant="primary"
          icon={<ToolJiraIcon />}
          disabled={!keyValid || !summary.trim()}
          onClick={() => onConfirm({ key, type, summary, assignee, status })}
        >
          Add story
        </Button>
      }
    >
      <ModalContent padded>
        <div className="space-y-4">
          <Banner
            variant="inline"
            type="info"
            title="Track a JIRA story on this request"
            description="Enter the JIRA key of a story to track it against this request. Its status mirrors JIRA and counts toward readiness to fulfil."
            canClose={false}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Control hideSpacing="bottom">
              <Label htmlFor="link-key">Issue key</Label>
              <Input
                id="link-key"
                placeholder="ONERING-1234"
                value={key}
                onChange={(e) => setKey(e.target.value)}
              />
            </Control>
            <Control hideSpacing="bottom">
              <Label htmlFor="link-type">Issue type</Label>
              <Select
                id="link-type"
                value={type}
                onChange={(e) => setType(e.target.value as JiraIssue['type'])}
              >
                <SelectOption value="Story" label="Story">
                  Story
                </SelectOption>
                <SelectOption value="Task" label="Task">
                  Task
                </SelectOption>
                <SelectOption value="Spike" label="Spike">
                  Spike
                </SelectOption>
                <SelectOption value="Bug" label="Bug">
                  Bug
                </SelectOption>
              </Select>
            </Control>
          </div>
          <Control hideSpacing="bottom">
            <Label htmlFor="link-summary">Summary</Label>
            <Input
              id="link-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />
          </Control>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Control hideSpacing="bottom">
              <Label htmlFor="link-assignee">Assignee</Label>
              <Input
                id="link-assignee"
                placeholder="Unassigned"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
              />
            </Control>
            <Control hideSpacing="bottom">
              <Label htmlFor="link-status">Status</Label>
              <Select
                id="link-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as JiraStatus)}
              >
                {JIRA_FLOW.map((s) => (
                  <SelectOption key={s} value={s} label={JIRA_STATUS_META[s].label}>
                    {JIRA_STATUS_META[s].label}
                  </SelectOption>
                ))}
              </Select>
            </Control>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

/* -------------------------------------------------------------------------- */
/* Request (detail) tab                                                       */
/* -------------------------------------------------------------------------- */

const RequestTab = ({
  requirement,
  viewAs,
  onEdit,
  onSubmit,
  onSendBackToClient,
  onSendToDevelopment,
  onSendBackToOps,
  onFulfil,
  onAddAttachments,
  onRemoveAttachment,
  onDelete,
  onRequestDeletion,
  onConfirmDeletion,
  onRejectDeletion,
}: {
  requirement: Requirement;
  viewAs: ActorRole;
  onEdit: () => void;
  onSubmit: () => void;
  onSendBackToClient: () => void;
  onSendToDevelopment: () => void;
  onSendBackToOps: () => void;
  onFulfil: () => void;
  onAddAttachments: (files: { name: string; size: number }[]) => void;
  onRemoveAttachment: (id: string) => void;
  onDelete: () => void;
  onRequestDeletion: () => void;
  onConfirmDeletion: () => void;
  onRejectDeletion: () => void;
}) => {
  const status = requirement.status;
  const readyToFulfil = allJiraDone(requirement);
  const pendingDeletion = !!requirement.cancellation && !requirement.cancellation.confirmed;

  const clientActions =
    viewAs === 'client' && status !== 'fulfilled' ? (
      <>
        <Button variant="secondary" icon={<EditIcon />} onClick={onEdit}>
          Edit
        </Button>
        {status === 'draft' && (
          <Button variant="primary" icon={<SendIcon />} onClick={onSubmit}>
            Submit for review
          </Button>
        )}
      </>
    ) : null;

  const opsActions =
    viewAs === 'account_manager' ? (
      <>
        {status === 'in_account_manager' && (
          <>
            <Button
              variant="destructive-secondary"
              icon={<UndoIcon />}
              onClick={onSendBackToClient}
            >
              Send back to client
            </Button>
            <Button variant="primary" icon={<ToolJiraIcon />} onClick={onSendToDevelopment}>
              Send to development
            </Button>
          </>
        )}
        {status === 'in_development' && (
          <Button
            variant="primary"
            icon={<SuccessIcon />}
            disabled={!readyToFulfil}
            onClick={onFulfil}
          >
            {readyToFulfil ? 'Mark fulfilled' : 'Waiting on development'}
          </Button>
        )}
      </>
    ) : null;

  const devActions =
    viewAs === 'developer' && status === 'in_development' ? (
      <Button
        variant="destructive-secondary"
        icon={<UndoIcon />}
        onClick={onSendBackToOps}
      >
        Send back to account manager
      </Button>
    ) : null;

  const hasActions = clientActions || opsActions || devActions;

  const deleteAction = (() => {
    if (status === 'cancelled' || pendingDeletion) return null;
    if (status === 'draft' && (viewAs === 'client' || viewAs === 'account_manager')) {
      return (
        <Button variant="destructive-secondary" icon={<TrashIcon />} onClick={onDelete}>
          Delete draft
        </Button>
      );
    }
    if (status !== 'fulfilled') {
      if (viewAs === 'client') {
        return (
          <Button
            variant="destructive-secondary"
            icon={<TrashIcon />}
            onClick={onRequestDeletion}
          >
            Request deletion
          </Button>
        );
      }
      if (viewAs === 'account_manager') {
        return (
          <Button
            variant="destructive-secondary"
            icon={<TrashIcon />}
            onClick={onConfirmDeletion}
          >
            Delete request
          </Button>
        );
      }
    }
    return null;
  })();

  return (
    <div className="space-y-4">
      {/* Status labels first — this is the first thing a user reads. */}
      {status === 'cancelled' && (
        <Banner
          variant="inline"
          type="error"
          title="Cancelled"
          description={
            requirement.cancellation?.reason
              ? `This request was cancelled. Reason: ${requirement.cancellation.reason}`
              : 'This request was cancelled and is kept as a read-only record.'
          }
          canClose={false}
        />
      )}
      {status === 'fulfilled' && (
        <Banner
          variant="inline"
          type="success"
          title="Fulfilled"
          description="This request has been delivered and marked fulfilled."
          canClose={false}
        />
      )}
      {pendingDeletion && requirement.cancellation && (
        <Banner
          variant="inline"
          type="warning"
          title="Deletion requested — awaiting account manager"
          description={`${ROLE_LABEL[requirement.cancellation.requestedByRole]} requested deletion at the ${STATUS_META[requirement.cancellation.cancelledAtStatus].label} stage.${
            requirement.cancellation.reason
              ? ` Reason: ${requirement.cancellation.reason}`
              : ''
          }`}
          canClose={false}
        />
      )}

      <div>
        <Divider subtitle="Requirements" />
        <pre className="mt-3 whitespace-pre-wrap break-words rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-relaxed text-color-secondary dark:border-gray-800 dark:bg-gray-950/40">
          {requirement.body}
        </pre>
      </div>

      <AttachmentsSection
        requirement={requirement}
        onAdd={onAddAttachments}
        onRemove={onRemoveAttachment}
      />

      {status !== 'cancelled' && status !== 'fulfilled' && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>{deleteAction}</div>
          <div className="flex flex-wrap items-center gap-2">
            {pendingDeletion ? (
              viewAs === 'account_manager' ? (
                <>
                  <Button variant="secondary" onClick={onRejectDeletion}>
                    Reject
                  </Button>
                  <Button
                    variant="destructive-primary"
                    icon={<TrashIcon />}
                    onClick={onConfirmDeletion}
                  >
                    Confirm deletion
                  </Button>
                </>
              ) : null
            ) : (
              <>
                {clientActions}
                {opsActions}
                {devActions}
              </>
            )}
            {!pendingDeletion && !hasActions && !deleteAction && (
              <p className="text-xs text-color-tertiary">
                No actions for the {ROLE_LABEL[viewAs]} role at the{' '}
                {STATUS_META[status].label} stage.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

type DetailTab = 'request' | 'development' | 'history' | 'discussion';

const IntakePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { requirements } = useIntakeStore();
  const [viewAs, setViewAs] = useState<ActorRole>('account_manager');
  const [detailTab, setDetailTab] = useState<DetailTab>('request');
  const [linkingJira, setLinkingJira] = useState(false);
  const [blockNoStories, setBlockNoStories] = useState(false);

  // Return trips from the "Create JIRA story" page ask us to focus a tab.
  useEffect(() => {
    const requestedTab = (location.state as { detailTab?: DetailTab } | null)?.detailTab;
    if (requestedTab) {
      setDetailTab(requestedTab);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const selected = useMemo(
    () => requirements.find((requirement) => requirement.id === id),
    [id, requirements],
  );
  useDocumentTitle(
    selected
      ? `${selected.reference} — Intake — OneRing Portal`
      : 'Request — Intake — OneRing Portal',
  );

  const actorName = useCallback(
    (role: ActorRole, req: Requirement): string =>
      role === 'client' ? req.clientName : ACTOR_NAME[role],
    [],
  );

  const patchRequirement = useCallback(
    (id: string, updater: (req: Requirement) => Requirement) => {
      setRequirements((prev) => prev.map((r) => (r.id === id ? updater(r) : r)));
    },
    [],
  );

  const pushTransition = useCallback(
    (
      to: RequirementStatus,
      type: RequirementEvent['type'],
      note?: string,
    ) => {
      if (!selected) return;
      const now = new Date().toISOString();
      const actor = actorName(viewAs, selected);
      patchRequirement(selected.id, (req) => ({
        ...req,
        status: to,
        updatedAt: now,
        events: [
          ...req.events,
          {
            id: newId(),
            type,
            fromStatus: req.status,
            toStatus: to,
            actor,
            actorRole: viewAs,
            createdAt: now,
            body: note,
          },
        ],
      }));
    },
    [actorName, patchRequirement, selected, viewAs],
  );

  const handleSendToDevelopment = useCallback(() => {
    if (!selected) return;
    if (selected.jiraIssues.length === 0) {
      setBlockNoStories(true);
      return;
    }
    const now = new Date().toISOString();
    patchRequirement(selected.id, (req) => ({
      ...req,
      status: 'in_development',
      updatedAt: now,
      events: [
        ...req.events,
        {
          id: newId(),
          type: 'status_changed',
          fromStatus: req.status,
          toStatus: 'in_development',
          actor: actorName('account_manager', req),
          actorRole: 'account_manager',
          createdAt: now,
          body: `Sent to development (${req.jiraIssues.length} JIRA ${
            req.jiraIssues.length === 1 ? 'story' : 'stories'
          }).`,
        },
      ],
    }));
    setDetailTab('development');
  }, [actorName, patchRequirement, selected]);

  const handleSyncJira = useCallback(() => {
    if (!selected) return;
    const now = new Date().toISOString();
    patchRequirement(selected.id, (req) => {
      const jiraIssues = req.jiraIssues.map((issue) => {
        if (issue.status === 'done') return issue;
        const next =
          JIRA_FLOW[Math.min(JIRA_FLOW.indexOf(issue.status) + 1, JIRA_FLOW.length - 1)]!;
        return { ...issue, status: next as JiraStatus };
      });
      return {
        ...req,
        updatedAt: now,
        jiraIssues,
        events: [
          ...req.events,
          {
            id: newId(),
            type: 'jira_updated',
            actor: actorName('developer', req),
            actorRole: 'developer',
            createdAt: now,
            body: jiraIssues
              .map((i) => `${i.key} → ${JIRA_STATUS_META[i.status].label}`)
              .join(', '),
          },
        ],
      };
    });
  }, [actorName, patchRequirement, selected]);

  const handleLinkJira = useCallback(
    (input: { key: string; type: JiraIssue['type']; summary: string; assignee: string; status: JiraStatus }) => {
      if (!selected) return;
      const now = new Date().toISOString();
      const key = input.key.trim().toUpperCase();
      const issue: JiraIssue = {
        key,
        type: input.type,
        summary: input.summary.trim(),
        status: input.status,
        assignee: input.assignee.trim() || 'Unassigned',
        url: `https://honeywell.atlassian.net/browse/${key}`,
      };
      patchRequirement(selected.id, (req) => ({
        ...req,
        updatedAt: now,
        jiraIssues: [...req.jiraIssues, issue],
        events: [
          ...req.events,
          {
            id: newId(),
            type: 'jira_updated',
            actor: actorName(viewAs, req),
            actorRole: viewAs,
            createdAt: now,
            body: `Linked ${key} (${JIRA_STATUS_META[input.status].label}).`,
          },
        ],
      }));
      setLinkingJira(false);
    },
    [actorName, patchRequirement, selected, viewAs],
  );

  const handleAddAttachments = useCallback(
    (files: { name: string; size: number }[]) => {
      if (!selected) return;
      const now = new Date().toISOString();
      const actor = actorName(viewAs, selected);
      patchRequirement(selected.id, (req) => {
        const attachments: Attachment[] = files.map((f) => ({
          id: newId(),
          name: f.name,
          size: f.size,
          uploadedBy: actor,
          uploadedByRole: viewAs,
          uploadedAt: now,
        }));
        return {
          ...req,
          updatedAt: now,
          attachments: [...req.attachments, ...attachments],
          events: [
            ...req.events,
            {
              id: newId(),
              type: 'attachment_added',
              actor,
              actorRole: viewAs,
              createdAt: now,
              body: files.map((f) => f.name).join(', '),
            },
          ],
        };
      });
    },
    [actorName, patchRequirement, selected, viewAs],
  );

  const handleRemoveAttachment = useCallback(
    (attachmentId: string) => {
      if (!selected) return;
      patchRequirement(selected.id, (req) => ({
        ...req,
        attachments: req.attachments.filter((a) => a.id !== attachmentId),
      }));
    },
    [patchRequirement, selected],
  );

  const handleComment = useCallback(
    (body: string) => {
      if (!selected) return;
      const now = new Date().toISOString();
      patchRequirement(selected.id, (req) => ({
        ...req,
        events: [
          ...req.events,
          {
            id: newId(),
            type: 'comment',
            actor: actorName(viewAs, req),
            actorRole: viewAs,
            createdAt: now,
            body,
          },
        ],
      }));
    },
    [actorName, patchRequirement, selected, viewAs],
  );

  const [deleteConfirm, setDeleteConfirm] = useState<
    null | 'delete-draft' | 'request-deletion' | 'confirm-deletion'
  >(null);

  const doDeleteDraft = useCallback(() => {
    if (!selected) return;
    const removedId = selected.id;
    setRequirements((prev) => prev.filter((requirement) => requirement.id !== removedId));
    navigate('/intake');
  }, [navigate, selected]);

  const doRequestDeletion = useCallback(() => {
    if (!selected) return;
    const now = new Date().toISOString();
    const actor = actorName(viewAs, selected);
    patchRequirement(selected.id, (req) => ({
      ...req,
      updatedAt: now,
      cancellation: {
        requestedBy: actor,
        requestedByRole: viewAs,
        cancelledAtStatus: req.status,
        requestedAt: now,
        confirmed: false,
      },
      events: [
        ...req.events,
        {
          id: newId(),
          type: 'deletion_requested',
          actor,
          actorRole: viewAs,
          createdAt: now,
          body: `Requested deletion at the ${STATUS_META[req.status].label} stage.`,
        },
      ],
    }));
  }, [actorName, patchRequirement, selected, viewAs]);

  const doConfirmDeletion = useCallback(() => {
    if (!selected) return;
    const now = new Date().toISOString();
    const actor = actorName('account_manager', selected);
    patchRequirement(selected.id, (req) => {
      const requestedBy = req.cancellation?.requestedBy ?? actor;
      const requestedByRole = req.cancellation?.requestedByRole ?? 'account_manager';
      const cancelledAtStatus = req.cancellation?.cancelledAtStatus ?? req.status;
      const requestedAt = req.cancellation?.requestedAt ?? now;
      return {
        ...req,
        status: 'cancelled',
        updatedAt: now,
        cancellation: {
          requestedBy,
          requestedByRole,
          cancelledAtStatus,
          requestedAt,
          reason: req.cancellation?.reason,
          confirmed: true,
          confirmedBy: actor,
          confirmedAt: now,
        },
        events: [
          ...req.events,
          {
            id: newId(),
            type: 'cancelled',
            fromStatus: req.status,
            toStatus: 'cancelled',
            actor,
            actorRole: 'account_manager',
            createdAt: now,
            body: 'Confirmed deletion — request cancelled.',
          },
        ],
      };
    });
  }, [actorName, patchRequirement, selected]);

  const doRejectDeletion = useCallback(() => {
    if (!selected) return;
    const now = new Date().toISOString();
    const actor = actorName('account_manager', selected);
    patchRequirement(selected.id, (req) => {
      const { cancellation: _dropped, ...rest } = req;
      return {
        ...rest,
        updatedAt: now,
        events: [
          ...req.events,
          {
            id: newId(),
            type: 'deletion_rejected',
            actor,
            actorRole: 'account_manager',
            createdAt: now,
            body: 'Rejected the deletion request — the request stays active.',
          },
        ],
      };
    });
  }, [actorName, patchRequirement, selected]);

  const DELETE_CONFIRM_COPY: Record<
    NonNullable<typeof deleteConfirm>,
    { title: string; body: string; confirmLabel: string; run: () => void }
  > = {
    'delete-draft': {
      title: 'Delete draft',
      body: 'This draft will be permanently removed. This cannot be undone.',
      confirmLabel: 'Delete draft',
      run: doDeleteDraft,
    },
    'request-deletion': {
      title: 'Request deletion',
      body: 'The account managers will be asked to confirm before this request is cancelled. It stays active until they confirm.',
      confirmLabel: 'Request deletion',
      run: doRequestDeletion,
    },
    'confirm-deletion': {
      title: 'Confirm deletion',
      body: 'The request will be cancelled and kept as a read-only record. This cannot be undone.',
      confirmLabel: 'Confirm deletion',
      run: doConfirmDeletion,
    },
  };

  return (
    <div className="space-y-4 p-4">
      <Breadcrumbs
        crumbs={[
          { id: 'intake', href: '/intake', label: 'Intake' },
          {
            id: 'request',
            label: selected?.reference ?? 'Request not found',
            noAction: true,
          },
        ]}
        onChange={({ href }) => {
          if (href) navigate(href);
        }}
      />

      {selected ? (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-bold">{selected.title}</h2>
                <Badge color={STATUS_META[selected.status].color}>
                  {STATUS_META[selected.status].label}
                </Badge>
                <Badge color={PRIORITY_META[selected.priority].color} outline>
                  {PRIORITY_META[selected.priority].label}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-color-tertiary">
                <span className="font-mono">{selected.reference}</span>
                {' · '}
                {selected.clientName}
                {' · '}
                {selected.clientCompany}
                {' · Updated '}
                {relativeTime(selected.updatedAt)}
              </p>
            </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-color-tertiary">View as</span>
            <SegmentedControl
              id="view-as"
              size="small"
              onChange={(e) => setViewAs(e.target.value as ActorRole)}
            >
              <SegmentedControlButton
                id="view-client"
                value="client"
                defaultChecked={viewAs === 'client'}
              >
                Client
              </SegmentedControlButton>
              <SegmentedControlButton
                id="view-ops"
                value="account_manager"
                defaultChecked={viewAs === 'account_manager'}
              >
                Account Manager
              </SegmentedControlButton>
              <SegmentedControlButton
                id="view-dev"
                value="developer"
                defaultChecked={viewAs === 'developer'}
              >
                Development
              </SegmentedControlButton>
            </SegmentedControl>
          </div>
          </div>

          <Card>
            <CardContent className="space-y-4">
              <StageStepper requirement={selected} />

              <Tabs
                className="pl-3"
                value={detailTab}
                onChange={(id) => setDetailTab(id as DetailTab)}
              >
                <Tab
                  id="request"
                  active={detailTab === 'request'}
                  icon={<DescriptionIcon />}
                >
                  Request
                </Tab>
                <Tab
                  id="development"
                  active={detailTab === 'development'}
                  icon={<ProgressIcon />}
                >
                  {`Development (${selected.jiraIssues.length})`}
                </Tab>
                <Tab id="history" active={detailTab === 'history'} icon={<HistoryIcon />}>
                  {`History (${selected.versions.length})`}
                </Tab>
                <Tab
                  id="discussion"
                  active={detailTab === 'discussion'}
                  icon={<ChatIcon />}
                >
                  {`Discussion (${
                    selected.events.filter((e) => e.type === 'comment').length
                  })`}
                </Tab>
              </Tabs>

              <div>
                {detailTab === 'request' && (
                  <RequestTab
                    requirement={selected}
                    viewAs={viewAs}
                    onEdit={() => navigate(`/intake/${selected.id}/edit`)}
                    onSubmit={() => pushTransition('in_account_manager', 'submitted')}
                    onSendBackToClient={() =>
                      pushTransition('draft', 'status_changed', 'Returned to client')
                    }
                    onSendToDevelopment={handleSendToDevelopment}
                    onSendBackToOps={() =>
                      pushTransition(
                        'in_account_manager',
                        'status_changed',
                        'Returned to account manager',
                      )
                    }
                    onFulfil={() => pushTransition('fulfilled', 'fulfilled')}
                    onAddAttachments={handleAddAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                    onDelete={() => setDeleteConfirm('delete-draft')}
                    onRequestDeletion={() => setDeleteConfirm('request-deletion')}
                    onConfirmDeletion={() => setDeleteConfirm('confirm-deletion')}
                    onRejectDeletion={doRejectDeletion}
                  />
                )}
                {detailTab === 'development' && (
                  <DevelopmentTab
                    requirement={selected}
                    viewAs={viewAs}
                    onSync={handleSyncJira}
                    onLinkJira={() => setLinkingJira(true)}
                    onCreateJira={() =>
                      navigate(`/intake/${selected.id}/jira/new`, {
                        state: { actorRole: viewAs },
                      })
                    }
                  />
                )}
                {detailTab === 'history' && <HistoryTab requirement={selected} />}
                {detailTab === 'discussion' && (
                  <div className="h-[560px]">
                    <DiscussionTab requirement={selected} onComment={handleComment} />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent>
            <EmptyState
              icon={<DescriptionIcon />}
              description="This request could not be found."
            />
            <div className="mt-4 flex justify-center">
              <Button variant="primary" onClick={() => navigate('/intake')}>
                Back to Intake
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {blockNoStories && selected && (
        <Modal
          title="No JIRA stories yet"
          onModalClose={() => setBlockNoStories(false)}
          onBackdropClick={() => setBlockNoStories(false)}
          footerLeft={
            <Button variant="tertiary" onClick={() => setBlockNoStories(false)}>
              Cancel
            </Button>
          }
          footerRight={
            <Button
              variant="primary"
              icon={<ProgressIcon />}
              onClick={() => {
                setBlockNoStories(false);
                setDetailTab('development');
              }}
            >
              Go to Development
            </Button>
          }
        >
          <ModalContent padded>
            <p className="text-sm text-color-tertiary">
              This request has no JIRA stories associated with it yet. Create or
              link at least one story on the Development tab before sending it to
              development.
            </p>
          </ModalContent>
        </Modal>
      )}
      {linkingJira && selected && (
        <LinkJiraModal
          onClose={() => setLinkingJira(false)}
          onConfirm={handleLinkJira}
        />
      )}
      {deleteConfirm && (
        <Modal
          title={DELETE_CONFIRM_COPY[deleteConfirm].title}
          onModalClose={() => setDeleteConfirm(null)}
          onBackdropClick={() => setDeleteConfirm(null)}
          footerLeft={
            <Button variant="tertiary" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
          }
          footerRight={
            <Button
              variant="destructive-primary"
              icon={<TrashIcon />}
              onClick={() => {
                DELETE_CONFIRM_COPY[deleteConfirm].run();
                setDeleteConfirm(null);
              }}
            >
              {DELETE_CONFIRM_COPY[deleteConfirm].confirmLabel}
            </Button>
          }
        >
          <ModalContent padded>
            <p className="text-sm text-color-secondary">
              {DELETE_CONFIRM_COPY[deleteConfirm].body}
            </p>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default IntakePage;
